import { useEffect, useState } from "react";
import { HiOutlineLocationMarker, HiOutlineClock } from "react-icons/hi";
import { motion } from "framer-motion";

import IFHeading from "../components/IF_Heading";
import { getContact, getContactCache } from "../services";

function SafeJsonParse(str, fallback = []) {
    try {
        return JSON.parse(str);
    } catch {
        return fallback;
    }
}

export default function Contact() {
    const cached = getContactCache();
    const [data, setDatas] = useState(() => cached || []);
    const [load, setLoad] = useState(() => !cached || cached.length === 0);

    useEffect(() => {
        getContact().then((res) => {
            if (res && res.length > 0) {
                setDatas(res);
            }
            setLoad(false);
        });
    }, []);

    if (load || !data || data.length === 0) {
        return (
            <div className="min-h-[70vh] w-full flex items-center justify-center">
                <div className="w-12 h-12 rounded-full border-4 border-white/20 border-t-pink animate-spin" />
            </div>
        );
    }

    const businessHours = data[0]?.value || "";
    const socials = SafeJsonParse(data[1]?.value, []);
    const locations = SafeJsonParse(data[2]?.value, []);

    return (
        <div className="flex flex-col w-full overflow-x-hidden">
            {/* Contact Hero Banner */}
            <section className="relative pt-[130px] lg:pt-[160px] pb-12 md:pb-20 overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div
                        className="w-full h-full bg-cover bg-bottom opacity-30"
                        style={{ backgroundImage: "url('/images/bg.jpg')" }}
                    />
                    <div className="absolute bottom-0 left-0 right-0 h-44 bg-gradient-to-t from-[#08090d] to-transparent" />
                </div>

                <div className="max-w-[1200px] mx-auto relative z-10 px-6">
                    <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
                        {/* Logo Emblem */}
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.8, ease: "linear" }}
                            className="shrink-0"
                        >
                            <div className="w-[130px] md:w-[170px] h-[130px] md:h-[170px] rounded-full bg-white p-5 border-3 border-pink flex items-center justify-center">
                                <img
                                    src="/logo-crop.png"
                                    alt="I-FIT"
                                    className="w-full h-full object-contain"
                                />
                            </div>
                        </motion.div>

                        {/* Contact Info Cards */}
                        <div className="flex flex-col gap-6 flex-1 w-full">
                            <div>
                                <h1 className="text-2xl sm:text-3xl md:text-4xl font-black bg-gradient-to-r from-white to-pink bg-clip-text text-transparent uppercase tracking-tight mb-1">
                                    HUBUNGI I-FIT INDONESIA
                                </h1>
                                <p className="text-xs sm:text-sm text-gray-400 font-medium">
                                    Pusat Solusi Perbaikan Postur Tulang Belakang &amp; Spesialis Kebugaran
                                </p>
                            </div>

                            <div className="p-5 rounded-2xl bg-[#12141e] border border-white/10 w-full">
                                <div className="flex items-center gap-2 mb-2">
                                    <HiOutlineClock className="text-pink text-xl" />
                                    <h4 className="text-xs font-black text-pink uppercase tracking-widest">
                                        Operating Hours
                                    </h4>
                                </div>
                                <p className="text-sm md:text-base text-gray-200 whitespace-pre-line font-medium leading-relaxed">
                                    {businessHours}
                                </p>
                            </div>

                            <div className="p-5 rounded-2xl bg-[#12141e] border border-white/10 w-full">
                                <div className="flex items-center gap-2 mb-2">
                                    <HiOutlineLocationMarker className="text-biruMuda text-xl" />
                                    <h4 className="text-xs font-black text-biruMuda uppercase tracking-widest">
                                        Studio Locations
                                    </h4>
                                </div>
                                <div className="flex flex-col gap-2">
                                    {locations.map((loc, idx) => (
                                        <p key={idx} className="text-xs sm:text-sm text-gray-300 leading-relaxed">
                                            • {loc.address}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Social Media Grid */}
            <section className="py-10 md:py-16 px-4 md:px-8">
                <IFHeading>CONNECT ON SOCIAL MEDIA</IFHeading>

                <div className="max-w-[1100px] mx-auto mt-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                        {socials.map((c, index) => (
                            <a
                                key={index}
                                href={c.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block"
                            >
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8, delay: index * 0.1, ease: "linear" }}
                                    whileHover={{ y: -6 }}
                                    className="p-5 rounded-2xl bg-[#12141e] border border-white/10 hover:border-pink transition-colors duration-200 flex items-center gap-4"
                                >
                                    <div className="w-14 h-14 rounded-full bg-white p-2 flex items-center justify-center shrink-0">
                                        <img
                                            src={process.env.REACT_APP_IMAGE_URL + c.icon}
                                            alt={c.text}
                                            className="w-full h-full object-contain"
                                        />
                                    </div>
                                    <div className="flex flex-col">
                                        <h4 className="text-base font-extrabold text-white">
                                            {c.text}
                                        </h4>
                                        <span className="text-xs text-gray-400">
                                            Follow Official Page &rarr;
                                        </span>
                                    </div>
                                </motion.div>
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            {/* Studio Maps Section */}
            <section className="py-10 md:py-16 px-4 md:px-8">
                <IFHeading>FIND OUR STUDIOS</IFHeading>

                <div className="max-w-[1200px] mx-auto mt-8 flex flex-col gap-12">
                    {locations.map((loc, index) => (
                        <div
                            key={index}
                            className="w-full p-5 md:p-8 rounded-3xl bg-[#12141e] border border-white/10 hover:border-pink/40 transition-colors duration-200"
                        >
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
                                {/* Image */}
                                <div className="rounded-2xl overflow-hidden border border-white/10 h-[240px] md:h-[300px]">
                                    <img
                                        src={process.env.REACT_APP_IMAGE_URL + loc.image}
                                        alt={loc.address}
                                        className="w-full h-full object-cover"
                                    />
                                </div>

                                {/* Map & Details */}
                                <div className="flex flex-col justify-between h-full gap-4">
                                    <div className="flex items-start gap-3">
                                        <HiOutlineLocationMarker className="text-pink text-2xl shrink-0 mt-1" />
                                        <div>
                                            <h4 className="text-lg font-extrabold text-white">
                                                Studio Location #{index + 1}
                                            </h4>
                                            <p className="text-xs sm:text-sm text-gray-300 mt-1 leading-relaxed">
                                                {loc.address}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="w-full h-[220px] md:h-[240px] rounded-2xl overflow-hidden border border-white/15">
                                        <iframe
                                            title={loc.address}
                                            src={loc.maps}
                                            className="w-full h-full border-0"
                                            allowFullScreen={true}
                                            loading="lazy"
                                            referrerPolicy="no-referrer-when-downgrade"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
